// knowledge-base-frontend/shared/hooks/useProjectsQuery.ts
"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  platformApi,
  Project,
  ProjectCreate,
  ProjectUpdate,
} from "@/shared/lib/platformApi";

// ========================================
// 🔑 CENTRALIZED QUERY KEYS
// ========================================
export const projectKeys = {
  all: ["projects"] as const,
  lists: () => [...projectKeys.all, "list"] as const,
  list: (filters?: Record<string, any>) =>
    [...projectKeys.lists(), filters] as const,
  details: () => [...projectKeys.all, "detail"] as const,
  detail: (uuid: string) => [...projectKeys.details(), uuid] as const,

  // Dashboard and Stats Keys
  dashboard: (uuid: string) =>
    [...projectKeys.detail(uuid), "dashboard"] as const,
  stats: (uuid: string) => [...projectKeys.detail(uuid), "stats"] as const,
  vectorStats: (uuid: string) =>
    [...projectKeys.stats(uuid), "vector"] as const, // Alias for compatibility

  // Documents/Sources Keys
  documents: (uuid: string) =>
    [...projectKeys.detail(uuid), "documents"] as const,
  documentList: (uuid: string, params?: any) =>
    [...projectKeys.documents(uuid), params] as const,

  // Name availability
  nameAvailability: (name: string) =>
    [...projectKeys.all, "name-check", name] as const,
};

// ========================================
// QUERIES
// ========================================

/**
 * Fetch all projects with optional filtering
 */
export function useProjectsQuery(params?: {
  page?: number;
  per_page?: number;
  include_inactive?: boolean;
  project_type?: string;
}) {
  return useQuery({
    queryKey: projectKeys.list(params),
    queryFn: () => platformApi.getProjects(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Fetch a single project by UUID
 */
export function useProjectQuery(projectUUID: string) {
  return useQuery({
    queryKey: projectKeys.detail(projectUUID),
    queryFn: () => platformApi.getProject(projectUUID),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!projectUUID,
  });
}

/**
 * Fetch project statistics
 */
export function useProjectStatsQuery(
  projectUUID: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: projectKeys.stats(projectUUID),
    queryFn: () => platformApi.getProjectStats(projectUUID),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? !!projectUUID,
  });
}

/**
 * Check if a project name (slug) is available.
 * Only queries when name is a non-empty string.
 */
export function useProjectNameAvailability(name: string) {
  return useQuery({
    queryKey: projectKeys.nameAvailability(name),
    queryFn: () => platformApi.checkProjectName(name),
    enabled: name.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000,
    retry: false,
  });
}

// ========================================
// MUTATIONS
// ========================================

/**
 * Create a new project
 */
export function useCreateProjectMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectData: ProjectCreate) =>
      platformApi.createProject(projectData),
    onSuccess: (newProject) => {
      // Invalidate and refetch projects list
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });

      // Optionally: Add the new project to the cache optimistically
      queryClient.setQueryData(projectKeys.detail(newProject.uuid), newProject);

      // Success handled by caller (CreateProjectDialog)
    },
    onError: (error) => {
      // Error handled by caller with context-specific messages
      if (process.env.NODE_ENV === "development") {
        console.error("Project creation failed:", error);
      }
    },
  });
}

/**
 * Update an existing project
 */
export function useUpdateProjectMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectUUID,
      projectData,
    }: {
      projectUUID: string;
      projectData: ProjectUpdate;
    }) => platformApi.updateProject(projectUUID, projectData),
    onMutate: async ({ projectUUID, projectData }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: projectKeys.detail(projectUUID),
      });

      // Snapshot the previous value
      const previousProject = queryClient.getQueryData<Project>(
        projectKeys.detail(projectUUID),
      );

      // Optimistically update the cache
      if (previousProject) {
        queryClient.setQueryData<Project>(projectKeys.detail(projectUUID), {
          ...previousProject,
          ...projectData,
          updated_at: new Date().toISOString(),
        });
      }

      return { previousProject };
    },
    onSuccess: (updatedProject, { projectUUID }) => {
      // Update the cache with the server response
      queryClient.setQueryData(projectKeys.detail(projectUUID), updatedProject);

      // Invalidate lists to ensure consistency
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
    onError: (error, { projectUUID }, context) => {
      // Rollback on error
      if (context?.previousProject) {
        queryClient.setQueryData(
          projectKeys.detail(projectUUID),
          context.previousProject,
        );
      }
      console.error("Failed to update project:", error);
    },
  });
}

/**
 * Delete a project permanently
 */
export function useDeleteProjectMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    // Now accepts an object with optional 'force' flag
    mutationFn: ({ uuid, force }: { uuid: string; force?: boolean }) =>
      platformApi.deleteProject(uuid, force),

    onSuccess: (_, { uuid }) => {
      // Destructure uuid from variables
      // Remove from cache
      queryClient.removeQueries({ queryKey: projectKeys.detail(uuid) });

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });

      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: projectKeys.stats(uuid) });
    },
    onError: (error) => {
      console.error("Failed to delete project:", error);
    },
  });
}
